// App.jsx — version complète et SEO-optimisée (remplace ton App.js)
import React from "react";
import { Helmet } from "react-helmet";
import { Routes, Route, Link } from "react-router-dom";
import './App.css';
import CookieBanner, { resetCookiesConsent } from "./components/CookieBanner";

// -------------------- CONFIG (Remplace ces valeurs) --------------------
const FORM_SPRING_ENDPOINT = "https://formspree.io/f/xnnoennl";
const CALENDLY_URL = "https://calendly.com/praticienreaccueil/consultation";
const SITE_URL = "https://www.re-accueil.com"; // <-- remplace par ton domaine réel
// -----------------------------------------------------------------------

const COLORS = {
  turquoise: "#30C6C3",
  black: "#0B0B0B",
  white: "#FFFFFF",
};

export default function App() {
  return (
    <div
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #30C6C326 100%)',
        color: COLORS.black,
        minHeight: '100vh',
        fontFamily:
          'Nunito, Poppins, system-ui, -apple-system, Roboto, "Helvetica Neue", Arial',
      }}
    >
      {/* Global SEO / OpenGraph / JSON-LD */}
      <Helmet>
        <title>Ré-accueil | Gestalt-thérapie à Rennes et en ligne</title>
        <meta
          name="description"
            content="Gestalt-thérapie à Rennes : accompagnement thérapeutique, individuel, groupes, ateliers créatifs et interventions en milieu professionnel."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/`} />

        {/* Open Graph */}
        <meta property="og:title" content="Ré-accueil — Gestalt-thérapie à Rennes" />
        <meta property="og:description" content="Séances individuelles, pour couples et sportifs. Approche centrée sur la présence et le corps." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:image" content={`${SITE_URL}/preview-card.png`} />

        {/* Twitter card */}
        <meta name="twitter:card" content="summary_large_image" />

        {/* JSON-LD LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Ré-accueil",
            telephone: "06 85 42 96 92",
            url: SITE_URL,
            address: {
              "@type": "PostalAddress",
              streetAddress: "Ton adresse ici",
              addressLocality: "Rennes",
              postalCode: "35000",
              addressCountry: "FR",
            },
            openingHours: "Mo-Fr 10:00-18:00",
            "image": "https://www.re-accueil.com/images/logo.png"
          })}
        </script>
      </Helmet>

      <Navbar />
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '32px' }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/approche-detaillee" element={<DetailedApproach />} />
          <Route path="/consultations" element={<Consultations />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/prendre-rendez-vous" element={<Booking />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
    </div> 
  );
}

/* ---------------------- Navbar ---------------------- */
function Navbar() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const mobileLinkStyle = {
    display: 'block',
    padding: '12px 16px',
    textDecoration: 'none',
    color: COLORS.white,
    fontWeight: 600,
    borderBottom: '1px solid rgba(255,255,255,0.2)',
  };

  const navLinkStyle = { color: COLORS.black, textDecoration: 'none', fontWeight: 600 };

  return (
    <header style={{ borderBottom: '1px solid #eee', background: COLORS.white }}>
      <div style={{
        maxWidth: 1100,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        padding: '18px 24px',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
          <img src="/logo.png" alt="Logo Ré-accueil Gestalt-thérapie Rennes" style={{ height: 56 }} loading="lazy" />
        </Link>

        {isMobile ? (
          <div style={{ width: '100%', marginTop: 12 }}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                width: 56,
                height: 56,
                background: COLORS.turquoise,
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                fontSize: 24,
                color: COLORS.white,
                zIndex: 20,
              }}
              aria-expanded={menuOpen}
              aria-controls="main-navigation"
              aria-label="Ouvrir le menu"
            >
              {menuOpen ? '✕' : '☰'}
            </button>

            <div
              id="main-navigation"
              style={{
                marginTop: 12,
                width: '100%',
                background: COLORS.turquoise,
                borderRadius: 12,
                overflow: 'hidden',
                maxHeight: menuOpen ? 500 : 0,
                transition: 'max-height 0.3s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Link to="/" style={mobileLinkStyle} onClick={() => setMenuOpen(false)}>Accueil</Link>
              <Link to="/a-propos" style={mobileLinkStyle} onClick={() => setMenuOpen(false)}>À propos</Link>
              <Link to="/approche-detaillee" style={mobileLinkStyle} onClick={() => setMenuOpen(false)}>Approche détaillée</Link>
              <Link to="/consultations" style={mobileLinkStyle} onClick={() => setMenuOpen(false)}>Consultations</Link>
              <Link to="/contact" style={mobileLinkStyle} onClick={() => setMenuOpen(false)}>Contact</Link>
              <Link to="/prendre-rendez-vous" style={mobileLinkStyle} onClick={() => setMenuOpen(false)}>Prendre rendez-vous</Link>
            </div>
          </div>
        ) : (
          <nav style={{ display: 'flex', gap: 18, alignItems: 'center' }} aria-label="Navigation principale">
            <Link to="/" style={navLinkStyle}>Accueil</Link>
            <Link to="/a-propos" style={navLinkStyle}>À propos</Link>
            <Link to="/approche-detaillee" style={navLinkStyle}>Approche détaillée</Link>
            <Link to="/consultations" style={navLinkStyle}>Consultations</Link>
            <Link to="/contact" style={navLinkStyle}>Contact</Link>
            <Link to="/prendre-rendez-vous" style={{
              background: COLORS.turquoise,
              color: COLORS.white,
              padding: '8px 14px',
              borderRadius: 18,
              textDecoration: 'none',
              fontWeight: 600,
            }}>
              Prendre rendez-vous
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}

/* ---------------------- Pages ---------------------- */

function Home() {
  return (
    <section>
      <Helmet>
        <title>Ré-accueil — Gestalt-thérapie et accompagnement | Rennes</title>
        <meta name="description" content="Un espace bienveillant pour explorer sensations, émotions et pensées. Séances en cabinet à Rennes et en ligne." />
        <link rel="canonical" href={`${SITE_URL}/`} />
      </Helmet>

      <div style={{ display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <h1 style={{ fontSize: 34, margin: 0, color: COLORS.black }}>
            Ré-accueil — Gestalt-thérapie et accompagnement à visée psychothérapeutique
          </h1>
          <p style={{ marginTop: 12, color: '#334155', fontSize: 16 }}>
            Un espace bienveillant pour explorer sensations, émotions et pensées. Séances en cabinet à Rennes et en ligne.
          </p>

          <div style={{ marginTop: 18, display: 'flex', gap: 12 }}>
            <Link to="/prendre-rendez-vous" style={{ padding: '10px 16px', borderRadius: 18, background: COLORS.turquoise, color: COLORS.white, textDecoration: 'none', fontWeight: 700 }}>
              Prendre rendez-vous
            </Link>
            <Link to="/contact" style={{ padding: '10px 16px', borderRadius: 18, border: '1px solid #e6e6e6', textDecoration: 'none', color: COLORS.black }}>
              Contact
            </Link>
          </div>

          <ul style={{ marginTop: 18, color: '#475569' }}>
            <li>📍 Cabinet à Rennes — consultations en ligne possibles</li>
            <li>🕊️ Approche centrée sur l'expérience présente</li>
          </ul>
        </div>
      </div>

      <section style={{ marginTop: 32 }}>
        <h2 style={{ fontSize: 20, marginBottom: 8 }}>Approche globale</h2>
        <p style={{ color: '#475569' }}>
          La Gestalt-thérapie que je pratique est une psychothérapie qui privilégie la relation, la conscience du vécu présent et la mise en acte.  
          J’accompagne différents profils vers plus de présence et d’efficacité émotionnelle :
        </p>

        <ul style={{ marginTop: 16, color: '#475569', lineHeight: 1.6 }}>
          <li><strong>Pour les personnes individuelles :</strong> comprendre leurs émotions, sensations et pensées afin de développer plus de présence et d’efficacité émotionnelle au quotidien.</li>
          <li><strong>Pour les couples :</strong> améliorer la communication et la présence mutuelle, favorisant une relation authentique et consciente.</li>
          <li><strong>Pour les sportifs :</strong> optimiser concentration, gestion du stress et performance émotionnelle.</li>
          <li><strong>Pour les professionnels et équipes :</strong> renforcer la présence, l’efficacité et la créativité.</li>
          <li><strong>Accompagnement ethnopsychiatrique :</strong> prise en compte des héritages culturels et des différences identitaires.</li>
        </ul>

        <div style={{ marginTop: 20 }}>
          <Link
            to="/approche-detaillee"
            style={{
              display: 'inline-block',
              padding: '10px 16px',
              borderRadius: 18,
              background: COLORS.turquoise,
              color: COLORS.white,
              textDecoration: 'none',
              fontWeight: 700,
            }}
          >
            Découvrir l’approche détaillée
          </Link>
        </div>
      </section>
    </section>
  );
}

function About() {
  return (
    <section>
      <Helmet>
        <title>À propos — Ré-accueil (Gestalt-thérapie à Rennes)</title>
        <meta name="description" content="Praticien en Gestalt-thérapie formé à l'ILFG. Parcours et valeurs : présence, bienveillance, créativité." />
        <link rel="canonical" href={`${SITE_URL}/a-propos`} />
      </Helmet>

      <h2 style={{ fontSize: 22 }}>À propos</h2>
      <p style={{ color: '#475569', marginBottom: 16 }}>
        Je suis praticien en Gestalt-thérapie, formé à l’ILFG à Limoges.  
        Mon parcours est un chemin de corps, d’esprit et de relation.
      </p>
      <p style={{ color: '#475569', marginBottom: 16 }}>
        Ancien sportif de haut niveau, j’ai longtemps exploré l’effort, le dépassement et la rigueur du geste.  
        Puis, comme éducateur sportif et ingénieur, chef de projet en informatique,  
        j’ai découvert d’autres terrains d’apprentissage : la précision, la réflexion, la complexité du monde de la data et du vivant.
      </p>

      <p style={{ color: '#475569', marginBottom: 16 }}>
        La Gestalt est venue relier ces univers — le corps en mouvement, l’esprit en quête de sens et la présence à soi.
      </p>

      <p style={{ color: '#475569', marginBottom: 16 }}>
        Depuis près de six ans, au fil de ma formation et de mes praticums,  
        j’accompagne des personnes dans des rencontres authentiques, autour de vraies problématiques humaines.
      </p>

      <p style={{ color: '#475569', marginBottom: 16 }}>
        Ensemble, nous apprenons à écouter ce qui se vit à l’intérieur :  
        les émotions — peur, joie, colère, honte, tristesse —, les sensations du corps, les pensées qui traversent.
      </p>

      <p style={{ color: '#475569', marginBottom: 16 }}>
        Ce travail ramène à la présence, à la respiration, à l’instant.  
        Peu à peu, quelque chose s’apaise, s’ouvre, se relie.  
        Être là, simplement, en lien avec soi.  
        C’est de là que naît la transformation.
      </p>
      <div className="grid-2-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title="Parcours" text="Formations, supervisons et spécialisations (formation trauma niveau 1 & 2, Gestalt, préparation mentale, etc.)." />
        <Card title="Valeurs" text="Présence, bienveillance, créativité et responsabilité." />
      </div>
    </section>
  );
}

function Card({ title, text }) {
  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 12, padding: 16 }}>
      <h4 style={{ margin: 0 }}>{title}</h4>
      <p style={{ color: '#475569', marginTop: 8 }}>{text}</p>
    </div>
  );
}

function Consultations() {
  return (
    <section>
      <Helmet>
        <title>Consultations — Ré-accueil</title>
        <meta name="description" content="Séances de 50 à 60 minutes — en cabinet à Rennes ou en visio. Modalités et accompagnements proposés." />
        <link rel="canonical" href={`${SITE_URL}/consultations`} />
      </Helmet>

      <h2 style={{ fontSize: 22 }}>Consultations</h2>
      <p style={{ color: '#475569' }}>Séances de 50 à 60 minutes — en cabinet à Rennes ou en visio. Modalités : paiement par virement, chèque ou espèce. Tarif sur demande.</p>

      <div className="grid-2-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <Card title="Individuel" text="Accompagnement thérapeutique sur la base d'une séance hebdomadaire ou à fréquence adaptée." />
        <Card title="Préparation mentale" text="Accompagnement orienté performance pour sportifs et professionnels." />
      </div>
    </section>
  );
}

function Booking() {
  return (
    <section>
      <Helmet>
        <title>Prendre rendez-vous — Ré-accueil</title>
        <meta name="description" content="Accédez au planning et réservez une séance via mon agenda en ligne." />
        <link rel="canonical" href={`${SITE_URL}/prendre-rendez-vous`} />
      </Helmet>

      <h2 style={{ fontSize: 22 }}>Prendre rendez-vous</h2>
      <p style={{ color: '#475569' }}>Pour réserver une séance, utilise la page Agenda dédiée ou contacte-moi via le formulaire.</p>

      <div style={{ marginTop: 18 }}>
        <a href={CALENDLY_URL} target="_blank" rel="noreferrer" 
           style={{ display: 'inline-block', padding: '12px 18px', borderRadius: 18, background: COLORS.turquoise, color: COLORS.white, fontWeight: 700, textDecoration: 'none' }}>
          Voir mon agenda
        </a>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = React.useState({ name: '', email: '', message: '' });
  const [status, setStatus] = React.useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('sending');

    try {
      const res = await fetch(FORM_SPRING_ENDPOINT, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, message: form.message })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error((data && data.error) || 'Erreur lors de l\'envoi');
      }

      setStatus('sent');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  }

  return (
    <section>
      <Helmet>
        <title>Contact — Ré-accueil</title>
        <meta name="description" content="Contactez Ré-accueil pour une séance, une question ou prise d'information. Réponse sous 48 heures." />
        <link rel="canonical" href={`${SITE_URL}/contact`} />
      </Helmet>

      <h2 style={{ fontSize: 22 }}>Contact</h2>
      <p style={{ color: '#475569' }}>Écrivez-moi un message et je vous réponds sous 48 heures.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, marginTop: 16 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label>
            <div style={{ fontSize: 14, marginBottom: 6 }}>Nom</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={{ padding: 12, borderRadius: 10, border: '1px solid #e6e6e6' }} />
          </label>

          <label>
            <div style={{ fontSize: 14, marginBottom: 6 }}>Email</div>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required style={{ padding: 12, borderRadius: 10, border: '1px solid #e6e6e6' }} />
          </label>

          <label>
            <div style={{ fontSize: 14, marginBottom: 6 }}>Message</div>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={6} style={{ padding: 12, borderRadius: 10, border: '1px solid #e6e6e6' }} />
          </label>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button type="submit" style={{ background: COLORS.turquoise, color: COLORS.white, padding: '10px 16px', borderRadius: 14, fontWeight: 700, border: 'none' }}>Envoyer</button>
            {status === 'sending' && <span style={{ color: '#64748b' }}>Envoi…</span>}
            {status === 'sent' && <span style={{ color: 'green' }}>Message envoyé — merci !</span>}
            {status === 'error' && <span style={{ color: 'red' }}>Erreur, réessaie plus tard.</span>}
          </div>
        </form>

        <aside style={{ border: '1px solid #f0f0f0', borderRadius: 12, padding: 16 }}>
          <h4 style={{ marginTop: 0 }}>Coordonnées</h4>
          <p style={{ color: '#475569', marginBottom: 12 }}>
            praticien@re-accueil.com<br />
            06 85 42 96 92<br />
            Cabinet à Rennes
          </p>

          <div>
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer"
               style={{ display: 'inline-block', padding: '8px 12px', borderRadius: 12, background: COLORS.turquoise, color: COLORS.white, textDecoration: 'none', fontWeight: 700 }}>
              Voir mon agenda
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}

function DetailedApproach() {
  return (
    <section style={{ paddingBottom: 40 }}>
      <Helmet>
        <title>Approche détaillée — Ré-accueil</title>
        <meta
          name="description"
          content="Approche thérapeutique fondée sur la Gestalt : séances individuelles, groupes, ateliers d'art-thérapie, interventions en entreprise et accompagnement du vivant."
        />
        <link rel="canonical" href={`${SITE_URL}/approche-detaillee`} />
      </Helmet>

      <h2 style={{ fontSize: 22 }}>Approche détaillée</h2>

      <p style={{ color: "#475569", lineHeight: 1.6 }}>
        <strong>Accompagnement thérapeutique à visée psychothérapeutique</strong>
        <br />
        J’accompagne les personnes, les groupes et les organisations dans un
        cadre thérapeutique fondé sur la Gestalt, une approche humaniste centrée
        sur la conscience du présent, la responsabilité personnelle et la
        qualité de la relation.
      </p>

      <p style={{ color: "#475569", lineHeight: 1.6 }}>
        Mon travail s’adresse à celles et ceux qui souhaitent mieux se
        connaître, retrouver du sens, apaiser leurs tensions intérieures ou
        développer leur potentiel relationnel et créatif. Chaque accompagnement
        est une exploration du vivant, de ce qui se joue dans l’instant, et de
        la manière dont chacun peut redevenir acteur de son mouvement intérieur.
      </p>

      <p style={{ color: "#475569", lineHeight: 1.6, marginTop: 24 }}>
        <strong>Séances individuelles et groupes de parole</strong>
        <br />
        Les séances individuelles offrent un espace d’écoute, de dialogue et
        d’ajustement à soi. Elles permettent de traverser des difficultés
        personnelles, affectives ou professionnelles, et d’expérimenter une
        présence plus authentique à soi et aux autres. Les groupes de parole
        invitent à partager, ressentir, et s’enrichir des expériences de chacun
        dans un cadre bienveillant et sécurisant.
      </p>

      <p style={{ color: "#475569", lineHeight: 1.6, marginTop: 24 }}>
        <strong>Ateliers d’art-thérapie et de théâtre (à venir)</strong>
        <br />
        Les ateliers d’art-thérapie et de théâtre ouvrent un champ d’expression
        libre et sensible. Le corps, la voix et le jeu deviennent des portes
        d’accès à ce qui ne se dit pas encore. Ces espaces favorisent la
        reconnexion au ressenti, la création de nouveaux possibles, et
        l’intégration émotionnelle dans un cadre collectif et soutenant.
      </p>

      <p style={{ color: "#475569", lineHeight: 1.6, marginTop: 24 }}>
        <strong>Accompagnement en milieu professionnel et individuel</strong>
        <br />
        En milieu professionnel, j’interviens pour soutenir la dynamique des
        équipes, la communication consciente, et la prévention des risques
        relationnels. Ces accompagnements visent à restaurer la qualité du lien,
        à renforcer la coopération et à donner du sens à l’action commune.
        J’accompagne également les sportifs, les artistes et toute personne en
        quête de cohérence entre performance, équilibre intérieur et
        épanouissement personnel — sans recherche de performance extérieure, mais
        dans la présence à soi et au geste juste.
      </p>

      <p style={{ color: "#475569", lineHeight: 1.6, marginTop: 24 }}>
        <strong>Une approche intégrative et vivante</strong>
        <br />
        Chaque rencontre est unique. Mon approche tisse ensemble la parole, le
        corps, le ressenti et la créativité, dans une alliance respectueuse du
        rythme et des besoins de chacun. C’est un chemin d’unification, à la
        fois thérapeutique, relationnel et profondément humain.
      </p>
    </section>
  );
}

/* ---------------------- Footer ---------------------- */
function Footer() {
  return (
    <footer style={{ borderTop: '1px solid #eee', marginTop: 40, padding: '20px 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <div style={{ color: '#94a3b8' }}>© {new Date().getFullYear()} Ré-accueil</div>
        <div style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>praticien@re-accueil.com · 06 85 42 96 92</span>
          <button
            onClick={resetCookiesConsent}
            title="Cliquez pour réinitialiser les cookies"
            style={{
              background: "transparent",
              border: "1px solid #30C6C3",
              color: "#30C6C3",
              borderRadius: 8,
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Gérer mes cookies
          </button>
        </div>
      </div>
    </footer>
  );
}

